// Live tab body: operational info for the user's relevant tournaments —
// individual schedule, next-game banner, pool standings/seed and playoff
// status per team (or the assigned-games schedule for referees).
// Everything renders from the live Firestore subscriptions; entries come
// from useLiveTournaments().

import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import { useFirebase } from './FirebaseContextProvider';
import { useDivisions, useTournamentParts } from '../utils/hooks';
import { buildColorLegend } from '../utils/colorLegend';
import { getTeamDivision, formatDateRange } from '../utils/misc';
import { findNextGame } from '../utils/live';
import { GameTypes } from '../utils/enums';
import { storageLiveTournament } from '../utils/consts';
import StandingsTable from './StandingsTable';
import LiveNextGameBanner from './LiveNextGameBanner';
import LiveSchedule from './LiveSchedule';
import LivePlayoffStatus from './LivePlayoffStatus';
import Spinner from './Spinner';
import placeholderTeam from '../assets/images/placeholder-team.png';
import './LiveTab.scss';

function LiveTeamSection({ tournament, teamId, now, colorLegend }) {
  const { teamsById, standingsByKey } = useFirebase();
  const team = teamsById.get(teamId);
  const division = getTeamDivision(team);

  const games = useMemo(
    () =>
      tournament.games.filter(
        (g) => g.team1 === teamId || g.team2 === teamId,
      ),
    [tournament.games, teamId],
  );

  const nextGame = findNextGame(tournament, games, now);

  const pool = useMemo(
    () =>
      standingsByKey.get(
        `${tournament.id}|${division}|${GameTypes.POOLGAME}`,
      ) || { rows: [], isComplete: false },
    [standingsByKey, tournament.id, division],
  );

  return (
    <section className='live-team-section'>
      <div className='team-section-header'>
        <div
          className='bg-image wh-64'
          style={{
            backgroundImage: `url(${team?.imgThumbUrl || placeholderTeam})`,
          }}
        />
        <div className='team-title-division'>
          <h5>{team?.name}</h5>
          <p className='division'>{division}</p>
        </div>
      </div>
      <LiveNextGameBanner
        tournament={tournament}
        game={nextGame}
        teamId={teamId}
        now={now}
      />
      <LiveSchedule
        tournament={tournament}
        games={games}
        teamId={teamId}
        now={now}
        nextGameId={nextGame?.id}
      />
      <LivePlayoffStatus
        tournament={tournament}
        teamId={teamId}
        division={division}
        pool={pool}
      />
      {pool.rows.length > 0 && (
        <div className='standings-wrap'>
          <StandingsTable
            id={tournament.id}
            division={division}
            gameType={GameTypes.POOLGAME}
            title='Pool standings'
            colorLegend={colorLegend}
            rows={pool.rows}
          />
        </div>
      )}
    </section>
  );
}

function LiveRefereeSection({ tournament, now }) {
  const { user } = useFirebase();

  const games = useMemo(
    () => tournament.games.filter((g) => g.referees?.includes(user.id)),
    [tournament.games, user.id],
  );

  const nextGame = findNextGame(tournament, games, now);

  return (
    <section className='live-team-section'>
      <LiveNextGameBanner tournament={tournament} game={nextGame} now={now} />
      <LiveSchedule
        tournament={tournament}
        games={games}
        now={now}
        nextGameId={nextGame?.id}
      />
    </section>
  );
}

export default function LiveTab({ entries }) {
  const location = useLocation();

  const [selectedId, setSelectedId] = useState(() => {
    const saved = sessionStorage.getItem(storageLiveTournament);
    return entries.some((e) => e.tournament.id === saved)
      ? saved
      : entries[0]?.tournament.id;
  });

  // Deep link (/live/{id}) preselect — also covers navigation while mounted
  useEffect(() => {
    const saved = sessionStorage.getItem(storageLiveTournament);
    if (!saved) return;
    sessionStorage.removeItem(storageLiveTournament);
    if (entries.some((e) => e.tournament.id === saved)) setSelectedId(saved);
  }, [location, entries]);

  // Selected tournament may drop out of entries mid-session (ended, team
  // removed) — fall back to the first entry
  const entry =
    entries.find((e) => e.tournament.id === selectedId) || entries[0];

  const [now, setNow] = useState(() => moment());
  useEffect(() => {
    if (!entry?.isLive) return;
    const interval = setInterval(() => setNow(moment()), 30000);
    return () => clearInterval(interval);
  }, [entry?.isLive]);

  // The selector may list several tournaments, but only the selected one is
  // rendered — and only it is read. `teams` is in the list for the division
  // colours below, not for this component's own sake.
  const arePartsReady = useTournamentParts(entry?.tournament.id, [
    'games',
    'fields',
    'teams',
    'standings',
  ]);

  const divisions = useDivisions(entry?.tournament.id);
  const colorLegend = useMemo(() => buildColorLegend(divisions), [divisions]);

  if (!entry) return null;

  const { tournament, myTeamIds, isRefereeMode, isLive } = entry;

  return (
    <div className='live-tab'>
      {entries.length > 1 && (
        <div className='live-selector'>
          {entries.map((e) => (
            <button
              type='button'
              className={`small bw${e.tournament.id === tournament.id ? ' active' : ''}`}
              onClick={() => setSelectedId(e.tournament.id)}
              key={e.tournament.id}
            >
              {e.tournament.name}
              {e.isLive && <span className='badge live'>LIVE</span>}
            </button>
          ))}
        </div>
      )}

      <div className='live-header'>
        <h4>
          {tournament.name}
          {isLive && <span className='badge live'>LIVE</span>}
        </h4>
        <div className='details'>
          <span className='detail'>
            <span className='fa-calendar gray-800' />
            {formatDateRange(tournament.startDate, tournament.endDate)}
          </span>
          {tournament.location && (
            <span className='detail'>
              <span className='fa-location gray-800' />
              {tournament.location}
            </span>
          )}
        </div>
      </div>

      {/* The schedule is read for the selected tournament alone, so switching
          tournaments waits for it. Rendering meanwhile would show an empty
          schedule and a next-game banner reading "Field TBD" — a wrong answer
          rather than a missing one. */}
      {!arePartsReady ? (
        <Spinner show inline size={40} />
      ) : isRefereeMode ? (
        <LiveRefereeSection tournament={tournament} now={now} />
      ) : (
        myTeamIds.map((teamId) => (
          <LiveTeamSection
            tournament={tournament}
            teamId={teamId}
            now={now}
            colorLegend={colorLegend}
            key={teamId}
          />
        ))
      )}

      <div className='live-links'>
        <Link to={`/tournaments/${tournament.id}#schedule`}>
          Full schedule
        </Link>
        <Link to={`/tournaments/${tournament.id}#brackets`}>Brackets</Link>
      </div>
    </div>
  );
}
